import { KalturaPinType } from '@kaltura-ott/ott-client-types/api/types';
import {
  CachingServiceImpl,
  DynamicDataTypeEnum,
  KuxMenuItem,
  pinUpdateParental,
  pinUpdatePurchase,
  pinValidateParental,
  PurchaseStates,
  toBoolean,
  tvpilGetLocalCache,
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
} from '@kaltura-ott/tvpil';
import { action, computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container, singleton } from 'tsyringe';

import {
  ParentalPinType,
  PinValidateStatus,
  PinLimitDetails,
  TimeOutDetails,
} from '../../types/stores/parental-pin-store-type';
import { errorHandling } from '../../utils/error-handling';
import type { AppStore } from '../app/app-store';
import { CategoryChildren } from '../category/category-children';
import { CategoryStore } from '../category/category-store';
import { SubpageStore } from '../category/subpage/subpage-store';
import { purchasePinValidate } from '../category/subpage/subscription';
import type { DmsStore } from '../dms';
import { Linear, LinkAsAsset, Subscription } from '../entities';
import { MediaAsset } from '../entities/MediaAsset';
import { UserStore } from '../user';

const DEFAULT_NO_THRESHOLD_AGE = '1000';

/**
 * @category Store
 */
@singleton()
export class ParentalPinStore {
  static moduleName = 'ParentalPinStore';

  @observable purchasePinEntered = false;

  public lastPagePinEnteredLink?: string;
  public lastPageLink?: string;
  @observable parentalPinEntered = {
    [ParentalPinType.adult]: false,
    [ParentalPinType.parental]: false,
  };

  @observable isPurchasePinLocked = false;
  @observable isParentalPinLocked = false;

  @observable purchasePinTimeout = 0;
  @observable parentalPinTimeout = 0;

  isInitiated = false;
  noThresholdAge = DEFAULT_NO_THRESHOLD_AGE;

  private wrongPinTriesLimit?: number;
  private wrongPinTimeout?: number;

  private dmsStore: DmsStore;
  private userStore: UserStore;
  private limitIntervalId: Record<KalturaPinType, NodeJS.Timeout | undefined> = {
    [KalturaPinType.purchase]: undefined,
    [KalturaPinType.parental]: undefined,
  };
  private cache!: CachingServiceImpl;

  public lastParentalPinEnteredValue = 0;
  public lastParentalPinProgramId = 0;

  constructor() {
    this.dmsStore = container.resolve<DmsStore>('dmsStore');
    this.userStore = container.resolve<UserStore>('userStore');
  }

  private get appStore() {
    return container.resolve<AppStore>('appStore');
  }

  init() {
    this.cache = tvpilGetLocalCache();
    this.wrongPinTimeout = this.dmsStore.dms.wrongPinTimeout;
    this.wrongPinTriesLimit = this.dmsStore.dms.wrongPinTriesLimit;
  }

  async initModal() {
    const purchasePinLimitDetails = await this.getPinLimit(KalturaPinType.purchase);
    const parentalPinLimitDetails = await this.getPinLimit(KalturaPinType.parental);

    if (this.wrongPinTriesLimit) {
      this.isPurchasePinLocked = purchasePinLimitDetails.limit >= this.wrongPinTriesLimit;
      this.isParentalPinLocked = parentalPinLimitDetails.limit >= this.wrongPinTriesLimit;
    }

    if (this.isPurchasePinLocked) {
      await this.lockPin(purchasePinLimitDetails);
    }
    if (this.isParentalPinLocked) {
      await this.lockPin(parentalPinLimitDetails);
    }
    this.isInitiated = true;
  }

  @computed
  get ageThreshold() {
    return (
      Number(this.userStore?.getFromDynamicData(DynamicDataTypeEnum.ageThreshold, this.userStore.userData)) || undefined
    );
  }

  @computed
  get pinForPurchase() {
    return toBoolean(
      this.userStore?.getFromDynamicData(DynamicDataTypeEnum.pinForPurchase, this.userStore.userData) || false,
    );
  }

  public isBlockByParentRating<T extends MediaAsset | CategoryStore<CategoryChildren>>(asset: T) {
    return computed(() => {
      if (asset instanceof CategoryStore) {
        if (asset.subtype === UIDAPresentationTypeEnum.parental_control) {
          return !this.parentalPinEntered[ParentalPinType.parental];
        }
        return asset.parentalPinProtected && !this.parentalPinEntered[ParentalPinType.parental];
      }
      if (asset instanceof Linear && this.lastParentalPinProgramId === asset?.currentProgram?.id) {
        return false;
      }
      return (
        !!this.ageThreshold &&
        asset.parentalRating > this.ageThreshold &&
        !this.parentalPinEntered[ParentalPinType.adult] &&
        this.lastParentalPinEnteredValue < asset.parentalRating &&
        this.lastParentalPinProgramId !== asset.id
      );
    }).get();
  }

  public isBlockedByParentalRating(parentalRating: number) {
    return (
      !!this.ageThreshold &&
      parentalRating > this.ageThreshold &&
      !this.parentalPinEntered[ParentalPinType.adult] &&
      this.lastParentalPinEnteredValue < parentalRating
    );
  }

  public isBlockByAdult<T extends MediaAsset | Subscription | CategoryStore<CategoryChildren>>(asset: T) {
    return computed(() => {
      // @ts-expect-error TODO Fix types
      return (asset.parentalPinProtected || asset.isAdult) && !this.parentalPinEntered[ParentalPinType.adult];
    }).get();
  }

  public get isAdultPinEntered() {
    return this.parentalPinEntered[ParentalPinType.adult];
  }

  public get isParentalPinEntered() {
    return this.parentalPinEntered[ParentalPinType.parental];
  }

  @action.bound
  @errorHandling()
  clearPinCodes() {
    this.parentalPinEntered[ParentalPinType.parental] = false;
    this.parentalPinEntered[ParentalPinType.adult] = false;
    this.lastParentalPinEnteredValue = 0;
    this.lastParentalPinProgramId = 0;
  }

  @action.bound
  @errorHandling()
  clearParentalPin(type: ParentalPinType) {
    this.parentalPinEntered[type] = false;

    // in case with parental pin have to be reset last entered limit as well
    if (type === ParentalPinType.parental) {
      this.lastParentalPinEnteredValue = 0;
    }
  }

  @action.bound
  @errorHandling()
  async updateParentalPin(pin: string) {
    return lastValueFrom(pinUpdateParental(pin));
  }

  @action.bound
  @errorHandling()
  async updatePurchasePin(pin: string) {
    return lastValueFrom(pinUpdatePurchase(pin));
  }

  /**
   * @param pin
   * @param type
   * @param asset
   */

  @action.bound
  @errorHandling()
  validatePin = async <T extends MediaAsset | Subscription | CategoryStore<CategoryChildren>>(
    pin: string,
    type: ParentalPinType,
    asset: T,
  ): Promise<{
    state: PinValidateStatus;
    message?: string;
    code?: string;
  }> => {
    try {
      await lastValueFrom(pinValidateParental(pin));
      this.parentalPinEntered[type] = true;

      if (type === ParentalPinType.adult) {
        this.parentalPinEntered[ParentalPinType.parental] = true;
      } else {
        if (asset instanceof MediaAsset) this.lastParentalPinEnteredValue = asset.parentalRating;
      }

      this.resetPinLock(KalturaPinType.parental);
      if (asset instanceof LinkAsAsset || asset instanceof SubpageStore) {
        if (asset instanceof SubpageStore) {
          this.lastPagePinEnteredLink = asset.referenceId;
        } else {
          this.lastPagePinEnteredLink = asset.data?.linkItem?.inAppLink;
        }
      } else if (this.lastPageLink) {
        this.lastPagePinEnteredLink = this.lastPageLink;
      } else {
        this.lastPagePinEnteredLink = '';
      }
      return {
        state: PinValidateStatus.ok,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (result: any) {
      this.setLimitToLocalCache(KalturaPinType.parental);

      return {
        state: PinValidateStatus.error,
        message: result.message,
        code: result.error,
      };
    }
  };

  @action.bound
  @errorHandling()
  validateAdultPin = async (
    pin: string,
  ): Promise<{
    state: PinValidateStatus;
    message?: string;
    code?: string;
  }> => {
    return this.validatePinAndReset(pin, () => {
      this.parentalPinEntered[ParentalPinType.adult] = true;
      this.parentalPinEntered[ParentalPinType.parental] = true;
      this.lastPagePinEnteredLink = this.lastPageLink ? this.lastPageLink : '';
    });
  };

  @action.bound
  @errorHandling()
  validateParentalRating = async ({
    pin,
    parentalRating,
  }: {
    pin: string;
    parentalRating: number;
  }): Promise<{
    state: PinValidateStatus;
    message?: string;
    code?: string;
  }> => {
    return this.validatePinAndReset(pin, () => {
      this.parentalPinEntered[ParentalPinType.parental] = true;
      this.lastParentalPinEnteredValue = parentalRating;
    });
  };

  @action.bound
  @errorHandling()
  validateInApplicationLink = async (
    pin: string,
    link: string,
  ): Promise<{
    state: PinValidateStatus;
    message?: string;
    code?: string;
  }> => {
    return this.validatePinAndReset(pin, () => {
      this.parentalPinEntered[ParentalPinType.adult] = true;
      this.parentalPinEntered[ParentalPinType.parental] = true;
      this.lastPagePinEnteredLink = link;
    });
  };

  @action
  @errorHandling()
  validatePurchasePin = async (
    pin: string,
  ): Promise<{
    state: string;
    message?: string;
    code?: string;
  }> => {
    try {
      await purchasePinValidate(pin);
      this.resetPinLock(KalturaPinType.purchase);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (result: any) {
      this.setLimitToLocalCache(KalturaPinType.purchase);

      this.purchasePinEntered = false;
      return {
        state: PurchaseStates.error,
        message: result.message,
        code: result.error,
      };
    }

    this.purchasePinEntered = true;
    return {
      state: PurchaseStates.ok,
    };
  };

  @errorHandling()
  checkMenuParentalPin = async (item: KuxMenuItem | CategoryStore): Promise<boolean | undefined> => {
    if (item instanceof CategoryStore) {
      return item.parentalPinProtected;
    }

    const { typeOfAppLink, inAppLink } = item;
    if (typeOfAppLink) {
      return this.appStore.rootStore?.children?.find(
        ({ type, referenceId }) => type === UIDAStructureTypeEnum.page && referenceId === inAppLink,
      )?.parentalPinProtected;
    }
    return false;
  };

  @action
  private async setLimitToLocalCache(pinType: KalturaPinType) {
    const { limit, pinItemName, timeOutName, timerName } = await this.getPinLimit(pinType);
    const newLimit = limit + 1;

    if (this.wrongPinTriesLimit && newLimit < this.wrongPinTriesLimit) {
      this.cache.setItem(pinItemName, newLimit, undefined);
    } else if (this.wrongPinTriesLimit && newLimit >= this.wrongPinTriesLimit) {
      this.lockPin({
        limit: newLimit,
        pinType,
        pinItemName,
        timeOutName,
        timerName,
      });
    }
  }

  @action
  async resetPinLock(pinType: KalturaPinType) {
    const { pinItemName, timeOutName, timerName } = await this.getPinLimit(pinType);
    this.cache.removeItem(pinItemName);
    this.cache.removeItem(timeOutName);
    this.cache.removeItem(timerName);
    this.setPinLock(pinType, false);
    this.destroy();
    this.limitIntervalId = {
      [KalturaPinType.purchase]: undefined,
      [KalturaPinType.parental]: undefined,
    };
  }

  public destroy() {
    if (this.limitIntervalId[KalturaPinType.parental]) clearInterval(this.limitIntervalId[KalturaPinType.parental]);
    if (this.limitIntervalId[KalturaPinType.purchase]) clearInterval(this.limitIntervalId[KalturaPinType.purchase]);
  }

  public setLastPageLink = (link: string) => {
    if (!link || link !== this.lastPagePinEnteredLink) {
      this.clearPinCodes();
    }

    this.lastPageLink = link;
  };

  @action
  private async lockPin({ pinType, limit, pinItemName, timeOutName, timerName }: PinLimitDetails) {
    if (!this.wrongPinTimeout || this.limitIntervalId[pinType]) {
      return;
    }
    const timeOut = Number(await this.cache.getItem(timeOutName));

    this.setPinLock(pinType, true);
    this.cache.setItem(pinItemName, limit, this.wrongPinTimeout * 1000);

    if (timeOut) {
      const timer = Math.floor(Number(await this.cache.getItem(timerName)) - (Date.now() - timeOut) / 1000);
      this.setPinTimeout({ pinType, timeOut: timer, timeStamp: Date.now(), timeOutName, timerName });
    } else {
      this.setPinTimeout({ pinType, timeOut: this.wrongPinTimeout, timeStamp: Date.now(), timeOutName, timerName });
    }

    this.limitIntervalId[pinType] = setInterval(async () => {
      const timer = Math.floor(
        this.getTimeout(pinType) - (Date.now() - Number(await this.cache.getItem(timeOutName))) / 1000,
      );

      this.setPinTimeout({ pinType, timeOut: Math.max(timer, 0), timeStamp: Date.now(), timeOutName, timerName });

      if (this.getTimeout(pinType) <= 0) {
        this.resetPinLock(pinType);
      }
    }, 5000);
  }

  private setPinLock(pinType: KalturaPinType, value: boolean) {
    switch (pinType) {
      case KalturaPinType.parental:
        this.isParentalPinLocked = value;
        break;
      case KalturaPinType.purchase:
        this.isPurchasePinLocked = value;
        break;
    }
  }

  private setPinTimeout({ pinType, timeOut, timeStamp, timeOutName, timerName }: TimeOutDetails) {
    this.cache.setItem(timeOutName, timeStamp, undefined);
    this.cache.setItem(timerName, timeOut, undefined);

    switch (pinType) {
      case KalturaPinType.parental:
        this.parentalPinTimeout = timeOut;
        break;
      case KalturaPinType.purchase:
        this.purchasePinTimeout = timeOut;
        break;
    }
  }

  private getTimeout(pinType: KalturaPinType) {
    switch (pinType) {
      case KalturaPinType.parental:
        return this.parentalPinTimeout;
      case KalturaPinType.purchase:
        return this.purchasePinTimeout;
    }
  }

  private async getPinLimit(pinType: KalturaPinType): Promise<PinLimitDetails> {
    const pinItemName = `${pinType}PinLimit`;
    const timeOutName = `${pinType}PinTimeout`;
    const timerName = `${pinType}PinTimer`;
    const limit = Number(await this.cache.getItem(pinItemName));

    return {
      limit,
      pinType,
      pinItemName,
      timeOutName,
      timerName,
    };
  }

  private validatePinAndReset = async (pin: string, onSuccess: () => void) => {
    try {
      await lastValueFrom(pinValidateParental(pin));
      this.resetPinLock(KalturaPinType.parental);

      onSuccess();

      return {
        state: PinValidateStatus.ok,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (result: any) {
      this.setLimitToLocalCache(KalturaPinType.parental);

      return {
        state: PinValidateStatus.error,
        message: result.message,
        code: result.error,
      };
    }
  };
}
